import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { DollarIcon, CalendarIcon } from './Icons'
import { API_BASE_URL } from '../utils/api'
import ModalShell from './ui/ModalShell'
import ModalFooter from './ui/ModalFooter'
import { buttonVariants } from './ui/buttonVariants'

interface CommissionSplit {
  id: number
  partner_id?: number
  split_type: 'percentage' | 'fixed_amount'
  split_value: number
  partner_name?: string
  partner_first_name?: string
  partner_last_name?: string
}

interface Deal {
  id: number
  name: string
  title: string
  amount: number
  base_points: number
  bonus_points: number
  volume_points: number
  customer_name?: string
  closing_date?: string
  commission_splits?: CommissionSplit[]
}

interface FundingConfirmationModalProps {
  deal: Deal
  isOpen: boolean
  onConfirm: (dealData: {
    amount: number
    base_points: number
    bonus_points: number
    volume_points: number
  }) => void
  onCancel: () => void
}

interface FinancialSettings {
  agencyFeePercentage: number
  closingFeeAmount: number
}

const FundingConfirmationModal: React.FC<FundingConfirmationModalProps> = ({
  deal,
  isOpen,
  onConfirm,
  onCancel
}) => {
  const { t } = useTranslation()
  const [editedDeal, setEditedDeal] = useState({
    amount: deal.amount,
    base_points: deal.base_points || 0,
    bonus_points: deal.bonus_points || 0,
    volume_points: deal.volume_points || 0
  })
  
  const [financialSettings, setFinancialSettings] = useState<FinancialSettings>({
    agencyFeePercentage: 0,
    closingFeeAmount: 0
  })

  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (isOpen) {
      fetchFinancialSettings()
    }
  }, [isOpen])

  const fetchFinancialSettings = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/settings/financial`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        setFinancialSettings({
          agencyFeePercentage: parseFloat(data.settings.find((s: any) => s.setting_key === 'agency_fee_percentage')?.setting_value || '0'),
          closingFeeAmount: parseFloat(data.settings.find((s: any) => s.setting_key === 'closing_fee_amount')?.setting_value || '0')
        })
      }
    } catch (error) {
      console.error('Error fetching financial settings:', error)
    }
  }

  const calculateCommission = () => {
    const totalPoints = editedDeal.base_points + editedDeal.bonus_points + editedDeal.volume_points
    const totalPercentage = totalPoints / 10000
    const grossCommission = editedDeal.amount * totalPercentage
    const agencyFee = grossCommission * (financialSettings.agencyFeePercentage / 100)
    const netCommission = grossCommission - agencyFee - financialSettings.closingFeeAmount
    
    const splits = (deal.commission_splits || []).map(split => {
      const amount = split.split_type === 'percentage'
        ? (Math.max(0, netCommission) * (split.split_value / 100))
        : split.split_value
      return {
        ...split,
        calculatedAmount: amount
      }
    })
    
    const totalSplitAmount = splits.reduce((sum, split) => sum + split.calculatedAmount, 0)
    const brokerCommission = Math.max(0, netCommission) - totalSplitAmount

    return {
      totalPoints,
      totalPercentage: totalPoints / 100,
      grossCommission,
      agencyFee,
      closingFeeAmount: financialSettings.closingFeeAmount,
      netCommission: Math.max(0, netCommission),
      splits,
      totalSplitAmount,
      brokerCommission: Math.max(0, brokerCommission)
    }
  }

  const handleConfirm = async () => {
    setLoading(true)
    try {
      await onConfirm(editedDeal)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0)
  }

  const commission = calculateCommission()

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onCancel}
      title={
        <span className="flex items-center gap-2">
          <span className="flex items-center justify-center w-8 h-8 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
            <DollarIcon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          </span>
          Confirm Deal Funding
        </span>
      }
      subtitle={deal.name || deal.title}
      size="lg"
    >
      <div className="space-y-4">
          <div className="mb-6">
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CalendarIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                    Final Commission Confirmation
                  </h3>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                    These values cannot be changed after the deal is funded. Please review and confirm all details.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {/* Deal Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Deal Amount
              </label>
              <input
                type="number"
                value={editedDeal.amount}
                onChange={(e) => setEditedDeal({ ...editedDeal, amount: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              />
            </div>

            {/* Commission Points */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Base Points
                </label>
                <input
                  type="number"
                  min="0"
                  max="999"
                  value={editedDeal.base_points}
                  onChange={(e) => setEditedDeal({ ...editedDeal, base_points: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Bonus Points
                </label>
                <input
                  type="number"
                  min="0"
                  max="999"
                  value={editedDeal.bonus_points}
                  onChange={(e) => setEditedDeal({ ...editedDeal, bonus_points: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Volume Points
                </label>
                <input
                  type="number"
                  min="0"
                  max="999"
                  value={editedDeal.volume_points}
                  onChange={(e) => setEditedDeal({ ...editedDeal, volume_points: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Commission Calculation */}
          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">{t('deals.dealDetail.commissionCalculation')}</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">{t('deals.dealDetail.totalPoints')}:</span>
                <span className="font-medium">{commission.totalPoints} ({commission.totalPercentage.toFixed(2)}%)</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">{t('deals.dealDetail.grossCommission')}:</span>
                <span className="font-medium">{formatCurrency(commission.grossCommission)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">{t('deals.dealDetail.agencyFee')} ({financialSettings.agencyFeePercentage}%):</span>
                <span className="font-medium text-red-600">-{formatCurrency(commission.agencyFee)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">{t('deals.dealDetail.closingFee')}:</span>
                <span className="font-medium text-red-600">-{formatCurrency(commission.closingFeeAmount)}</span>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-600 pt-2 mt-2">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-900 dark:text-white">{t('deals.dealDetail.netCommission')}:</span>
                  <span className="font-bold text-green-600 dark:text-green-400">{formatCurrency(commission.netCommission)}</span>
                </div>
              </div>
              
              {/* Commission Splits */}
              {commission.splits.length > 0 && (
                <>
                  <div className="border-t border-gray-200 dark:border-gray-600 pt-2 mt-2">
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                      {t('deals.dealDetail.commissionSplits')}
                    </div>
                    {commission.splits.map((split) => (
                      <div key={split.id} className="flex justify-between py-1">
                        <span className="text-gray-600 dark:text-gray-400">
                          {split.partner_name || `${split.partner_first_name} ${split.partner_last_name}`}
                          <span className="text-gray-400 dark:text-gray-500 text-xs ml-1">
                            ({split.split_type === 'percentage' ? `${split.split_value}%` : formatCurrency(split.split_value)})
                          </span>
                        </span>
                        <span className="font-medium text-red-600 dark:text-red-400">
                          -{formatCurrency(split.calculatedAmount)}
                        </span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Broker Commission (Your Commission) */}
                  <div className="border-t border-gray-200 dark:border-gray-600 pt-2 mt-2">
                    <div className="flex justify-between p-2 bg-green-50 dark:bg-green-900/30 rounded">
                      <span className="font-medium text-gray-900 dark:text-white">{t('deals.dealDetail.brokerCommission')}:</span>
                      <span className="font-bold text-green-600 dark:text-green-400">{formatCurrency(commission.brokerCommission)}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
      </div>

      <ModalFooter>
        <button onClick={onCancel} disabled={loading} className={buttonVariants.secondary}>
          Cancel
        </button>
        <button onClick={handleConfirm} disabled={loading} className={buttonVariants.success}>
          {loading ? 'Confirming…' : 'Confirm & Fund Deal'}
        </button>
      </ModalFooter>
    </ModalShell>
  )
}

export default FundingConfirmationModal
